import React, { useEffect } from 'react';

import { track, useAutoNavigate, useReturnToPath } from '@betterment/js-runtime';
import { Box, Text } from '@betterment/samba-react';
import { Button, Heading, Modal } from '@betterment/style-closet-react';

import Checking from './Checking';

import { CHECKING_SUPPORT_PHONE, SUPPORT_EMAIL } from '../../../Shared/constants';
import { locationAssign } from '../../../Shared/path-helpers';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { useUser } from '../../contexts/UserProvider';
import { CloseButton, ModalContent, ModalOverlay } from '../../modal-helpers';

const ContactSupportModalBody: React.FC = () => (
  <Text as="p">
    Thank you for your interest in Betterment&apos;s Checking account. To open your account, please contact us at{' '}
    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or{' '}
    <a href={`tel:${CHECKING_SUPPORT_PHONE}`}>{CHECKING_SUPPORT_PHONE}</a>.
  </Text>
);

const ManualReviewModalBody: React.FC = () => (
  <Text as="p">
    Thank you for your interest in our Checking account. We are currently reviewing your account, which typically takes
    one business day. Please try opening your Checking account later.
  </Text>
);

const DpoAddressModalBody: React.FC = () => (
  <Text as="p">
    Thank you for your interest in Checking. Unfortunately, we don&apos;t currently support your DPO address for
    Checking. If you need additional help to update your address, please contact us at{' '}
    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or{' '}
    <a href={`tel:${CHECKING_SUPPORT_PHONE}`}>{CHECKING_SUPPORT_PHONE}</a>.
  </Text>
);

const SuspendedIdUploadRequiredModalBody: React.FC = () => (
  <>
    <Text as="p">
      Your security is our top priority, so we need a few more pieces of information before we can complete your account
      setup.
    </Text>
    <Text
      css={{
        fontWeight: 'bold',
      }}
    >
      You will be restricted from finishing your account setup, connecting an external bank account, and depositing
      funds while your account is being reviewed.
    </Text>
  </>
);

const SuspendedUnderReviewModalBody: React.FC = () => (
  <Text as="p">
    We&apos;ve successfully received your documentation. We&apos;ll email you when we&apos;ve finished reviewing it,
    which is usually within 2-3 business days. You are welcome to explore Betterment in the meantime, but you will be{' '}
    <Text
      css={{
        fontWeight: 'bold',
      }}
    >
      restricted from finishing your Checking account setup, linking an external bank, and depositing funds until your
      account is approved.
    </Text>{' '}
    Contacting us will not expedite the review process.
  </Text>
);

const Splash: React.FC = () => {
  const { user } = useUser();
  const [shouldShowModal, setShowModal] = React.useState(false);
  const navigateRouter = useAutoNavigate();
  const returnToPath = useReturnToPath(urlBuilder.appHome.absolute());

  const handleModalClose = () => setShowModal(false);

  const handleClick = () => {
    if (user && user.checkingEligibilityStatus === 'eligible') {
      track('ElementClicked', { name: 'GetStarted' });
      void navigateRouter(urlBuilder.appCheckingSignups.absolute());
    } else {
      track('ElementViewed', { name: 'BlockedChecking' });
      setShowModal(true);
    }
  };

  const goToIdUpload = () => {
    handleModalClose();
    locationAssign(urlBuilder.getInvestingMultidocUploadPage.absolute());
  };

  let modalHeaderText = 'Thank you for your interest';
  if (user?.checkingEligibilityStatus === 'suspended') {
    modalHeaderText = 'Sit tight as we review your documentation';
  } else if (user?.checkingEligibilityStatus === 'suspended_documents_required') {
    modalHeaderText = 'We need additional information to verify your account';
  }

  const viewProps = { handleClick };

  useEffect(() => {
    track('ElementViewed', { name: 'CheckingExpectations' });
  }, []);

  return (
    <ModalOverlay>
      <ModalContent aria-label="checking signup">
        <CloseButton closeHandler={() => locationAssign(returnToPath)} />
        <Checking {...viewProps} />
        {shouldShowModal && (
          <Modal closeHandler={handleModalClose} label="checking unavailable">
            <Box
              css={{
                padding: 'space.400',
              }}
            >
              <Heading>{modalHeaderText}</Heading>
              <Box
                css={{
                  width: '80%',
                  marginBottom: 'space.400',
                }}
              >
                {user?.checkingEligibilityStatus === 'account_group_locked' && <ContactSupportModalBody />}
                {user?.checkingEligibilityStatus === 'manual_review' && <ManualReviewModalBody />}
                {user?.checkingEligibilityStatus === 'invalid_dpo_address' && <DpoAddressModalBody />}
                {user?.checkingEligibilityStatus === 'suspended' && <SuspendedUnderReviewModalBody />}
                {user?.checkingEligibilityStatus === 'suspended_documents_required' && (
                  <SuspendedIdUploadRequiredModalBody />
                )}
              </Box>
              {user?.checkingEligibilityStatus === 'suspended_documents_required' ? (
                <Button onClick={goToIdUpload}>Upload your ID</Button>
              ) : (
                <Button onClick={handleModalClose}>Close</Button>
              )}
            </Box>
          </Modal>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default Splash;
